import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useNotification } from 'config/Notification';

import {
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Warning from '@mui/icons-material/Warning';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { fetchReports as fetchReportsService, fetchReportCompanies as fetchReportCompaniesService, updateReportStatus as updateReportStatusService } from 'services/modules/ecs/report';
import { fetchDimensions as fetchDimensionsService } from 'services/modules/ecs/data';
import { deleteAlerts as deleteAlertsService } from 'services/modules/ecs/alert';


function RequestReportPage() {
  // State variables
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [expandedRows, setExpandedRows] = useState([]);
  const [innerPage, setInnerPage] = useState(0);
  const [innerRowsPerPage, setInnerRowsPerPage] = useState(5);
  const [reportId, setReportId] = useState('');
  const [dimensionId, setDimensionId] = useState('');
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reportsData, setReportsData] = useState([]);
  const reportOptions = [
    { label: 'OPEN', value: 'OPEN' },
    { label: 'IN PROGRESS', value: 'INPROGRESS' },
    { label: 'COMPLETED', value: 'COMPLETED' },
    { label: 'REVIEW', value: 'REVIEW' },
    { label: 'CANCELLED', value: 'CANCELLED' },
  ];
  const [totalReportsCount, setTotalReportsCount] = useState(0);

  // Redux selectors
  const reportCompaniesContainer = useSelector((state) => state.report.reportCompanies);
  const dimensionsContainer = useSelector((state) => state.ecsData.dimensions);
  const authenticatedUserContainer = useSelector((state) => state.auth.user);

  // React router
  const navigate = useNavigate();

  // Redux dispatch
  const dispatch = useDispatch();

  // Notification
  const { openNotification } = useNotification();

  // This method returns the dimension name 
  function getDimension(dim) {
    const dimension = dimensionsContainer.find((d) => !!d.subDimensions && d.subDimensions.find((sub) => sub.id === dim));
    if (dimension && dimension.label) {
      return dimension.label;
    }
    return '';
  }

  // Handles date formatting
  const formatDate = (date) => {
    const formattedDate = new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
    return formattedDate;
  };

  // Handles time formatting
  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString();
  };

  // Handles change in search query like company name or domain
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  // Handles page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handles change in rows per page
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handles page change for dimension table
  const handleInnerPageChange = (event, newPage) => {
    setInnerPage(newPage);
  };

  // Handles change in rows per page for dimension table
  const handleInnerRowsPerPageChange = (event) => {
    setInnerRowsPerPage(parseInt(event.target.value, 10));
    setInnerPage(0);
  };

  // Handles navigation to upload files component
  const handleReportsUpload = (reportId) => {
    navigate(`/crs/upload-files/${reportId}`);
  };

  // Handles navigation to review reports component
  const handleReviewReports = (id) => {
    navigate(`/crs/report/${id}`);
  };

  // Handles changes in status dropdown
  const handleStatusChange = async (newStatus, reportId) => {
    setLoading(true);
    const status = await dispatch(updateReportStatusService({ reportId, reportStatus: newStatus }));
    if (status.payload === 200) {
      openNotification("Report status changed successfully", 'success');
      fetchReportsData();
    }
    else {
      openNotification(status.payload, 'error');
    }
    setLoading(false)
  };
  // Handles displaying the alerts uploaded by user
  const handleExpandClick = async (reportId) => {
    setExpandedRows((prevExpandedRows) => {
      if (prevExpandedRows.includes(reportId)) {
        return prevExpandedRows.filter((id) => id !== reportId);
      } else {
        return [...prevExpandedRows, reportId];
      }
    });

    // Fetch sub-table data when expanding
    if (!expandedRows.includes(reportId)) {
      try {
        await dispatch(fetchDimensionsService());
      } catch (error) {
        openNotification('Error while expanding the sub-table', 'error')
      }
    }
  };


  const handleDeleteInnerRow = (dimensionId, reportId) => {
    setDimensionId(dimensionId);
    setReportId(reportId);
    setDeleteDialogOpen(true);
  };

  // Handles deleting the alert data
  const handleConfirmDelete = async () => {
    setDeleteDialogOpen(false);
    try {
      const response = await deleteAlertsService({ dimension: dimensionId, report: reportId });
      if (response === true) {
        openNotification("Alert deleted successfully", 'success');
        fetchReportsData();
      }
      else
        openNotification(response, 'error')
    }
    catch (error) {
      openNotification('Error while deleting the alert', 'error')
    }
  };

  // Handles displaying the inner table
  const renderInnerTableRows = (data, reportId) => {
    const startIndex = innerPage * innerRowsPerPage;
    const endIndex = startIndex + innerRowsPerPage;
    return data.slice(startIndex, endIndex).map((dimension) => (
      <TableRow key={dimension.id}>
        <TableCell className="text-black text-bold">{dimension.dimension.label}</TableCell>
        <TableCell className="text-black text-bold">{getDimension(dimension.dimension.id)}</TableCell>
        <TableCell>
          <Tooltip title="Delete Alerts" arrow>
            <IconButton onClick={() => handleDeleteInnerRow(dimension.dimension.id, reportId)}>
              <DeleteIcon className="text-secondary" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
    ));
  };

  // Handles fetching the reports data
  const fetchReportsData = async () => {
    setLoading(true);
    const params = { page: page + 1, limit: rowsPerPage, sortBy: '-createdAt' };
    if (searchQuery.trim() !== '') {
      params.search = searchQuery.trim();
    }
    if (selectedCompany) {
      params.domain = selectedCompany.domain;
    }
    try {
      let response = await fetchReportsService(params);
      if (response?.status === 200)
        setReportsData(response?.data)
      else
        openNotification(response);
    } finally {
      setLoading(false);
    }
  };

  // useEffect fetches report companies service
  useEffect(() => {
    const params = { project: "_id,name,domain" };
    dispatch(fetchReportCompaniesService(params));
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (selectedCompany?.domain !== null)
      fetchReportsData();
    // eslint-disable-next-line
  }, [page, rowsPerPage, searchQuery, selectedCompany]);

  useEffect(() => {
    if (dimensionsContainer && dimensionsContainer.length > 0) {
      let activeDimensions = dimensionsContainer.filter(dim => dim.status === true && dim.subDimensions);
      const filtereddimensions = activeDimensions.map(dim => dim.subDimensions).flat().filter(subDim => subDim.status === true);
      setTotalReportsCount(filtereddimensions.length);
    }
  }, [dimensionsContainer]);

  return (
    <div className="m-2">
      <div className="m-2">
        <div className="row">
          <div className="col-lg-3 col-md-3 col-sm-12 col-xs-12 search-ecs ">
            <TextField
              variant="outlined"
              placeholder="Search"
              value={searchQuery}
              fullWidth
              size="small"
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </div>
          <div className="col-lg-3 col-md-3 col-sm-12 col-xs-12 search-ecs ">
            {reportCompaniesContainer.results && (
              <Autocomplete
                options={reportCompaniesContainer.results || []}
                getOptionLabel={(option) => option?.name || ''}
                value={selectedCompany}
                onChange={(e, value) => setSelectedCompany(value)}
                renderInput={(params) => (
                  <TextField {...params} label="Company" variant="outlined" size="small" />
                )}
              />
            )}
          </div>
        </div>
        <div>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Report id</TableCell>
                  <TableCell>Requested by</TableCell>
                  <TableCell>Domain</TableCell>
                  <TableCell>Requested on</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Progress</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : reportsData?.results?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Warning color="black" fontSize="small" />
                      No data available
                    </TableCell>
                  </TableRow>
                ) : (
                  reportsData?.results?.map((result) => (
                    <React.Fragment key={result.id}>
                      <TableRow>
                        <TableCell className="name-table">
                          {result.reportStatus !== 'COMPLETED' &&
                            result.reportStatus !== 'CANCELLED' &&
                            result.progress &&
                            result.progress.length &&
                            authenticatedUserContainer.roles.some((role) =>
                              role.permissions.includes('DELETE_ALERTS')
                            ) ? (
                            <IconButton
                              size="small"
                              onClick={() => handleExpandClick(result.id)}
                            >
                              <ExpandMoreIcon />
                            </IconButton>
                          ) : null}
                          {result.reportId}
                        </TableCell>
                        <TableCell className="text-bold text-black">{result.requestedBy}</TableCell>
                        <TableCell className="text-secondary">{result.domain}</TableCell>
                        <TableCell>
                          <div className="date-formatter">
                            <p className="text-bold text-black m-0">{formatDate(result.createdAt)}</p>
                            <p className="text-primary subDim-text m-0" >{formatTime(result.createdAt)}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {authenticatedUserContainer.roles.some((role) => role.permissions.includes('UPDATE_REPORT')) ? (
                            result.reportStatus !== 'CANCELLED' && result.reportStatus !== 'COMPLETED' ? (
                              <Select
                                variant="outlined"
                                color="primary"
                                value={result.reportStatus}
                                onChange={(event) => handleStatusChange(event.target.value, result.id)}
                                size="small"
                                className="dropdown-font"
                              >
                                {reportOptions.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            ) : (
                              <Chip
                                variant="outlined"
                                className={`px-md py-sm ${result.reportStatus.toLowerCase()}`}
                                label={result.reportStatus.charAt(0) + result.reportStatus.toLowerCase().slice(1)}
                              />
                            )
                          ) : (
                            result.reportStatus
                          )}
                        </TableCell>
                        <TableCell>
                          {authenticatedUserContainer.roles.some((role) => role.permissions.includes('CREATE_ALERTS')) ? (
                            result.reportStatus === 'INPROGRESS' ? (
                              <p>
                                <span className="text-bold">
                                  {result.progress.length}/{totalReportsCount}
                                </span>
                                <span>
                                  ({((result?.progress?.length / totalReportsCount) * 100).toFixed(2)} %)

                                </span>
                              </p>
                            ) : (
                              result.reportStatus === 'REVIEW' ? (
                                <p>
                                  <span className="text-bold">
                                    {result.progress.filter((prog) => prog.reviewed).length}/{totalReportsCount}
                                  </span>
                                  <span>
                                    ({((result.progress.filter((prog) => prog.reviewed).length / totalReportsCount) * 100).toFixed(2)}%)
                                  </span>
                                </p>
                              ) : null
                            )
                          ) : (
                            result.reportStatus
                          )}
                        </TableCell>
                        <TableCell>
                          {authenticatedUserContainer.roles.some((role) => role.permissions.includes('CREATE_ALERTS') || role.permissions.includes('REVIEW_ALERTS')) ? (
                            result.reportStatus === 'REVIEW' ? (
                              <Button variant="contained" onClick={() => handleReviewReports(result.id)}>Review</Button>
                            ) : (
                              result.reportStatus === 'OPEN' || result.reportStatus === 'INPROGRESS' ? (
                                <Button variant="contained" onClick={() => handleReportsUpload(result.reportId)}>Upload</Button>
                              ) : null
                            )
                          ) : (
                            result.reportStatus
                          )}
                        </TableCell>
                      </TableRow>
                      {expandedRows.includes(result.id) && (
                        <TableRow>
                          <TableCell colSpan={7}>
                            <Table>
                              <TableHead>
                                <TableRow>
                                  <TableCell>Sub-Dimension</TableCell>
                                  <TableCell>Dimension</TableCell>
                                  <TableCell>Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {result.progress.length > 0 ? (
                                  renderInnerTableRows(result.progress, result.id)
                                ) : (
                                  <TableRow>
                                    <TableCell colSpan={3}>
                                      No data available
                                    </TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                            <TablePagination
                              component="div"
                              rowsPerPageOptions={[5, 10, 25]}
                              count={result.progress.length}
                              rowsPerPage={innerRowsPerPage}
                              page={innerPage}
                              onPageChange={handleInnerPageChange}
                              onRowsPerPageChange={handleInnerRowsPerPageChange}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component={Paper}
            rowsPerPageOptions={[5, 7, 10, 15, 20, 25, 50]}
            count={reportsData.totalResults || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
          <Dialog open={isDeleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} aria-labelledby="delete-dialog-title">
            <DialogTitle align="center">
              <ErrorOutlineIcon fontSize="large" color="error" />
            </DialogTitle>

            <DialogContent>
              Are you sure you want to delete the alerts in this sub-dimension?
            </DialogContent>
            <DialogActions className="m-3">
              <Button onClick={() => setDeleteDialogOpen(false)} variant="contained" className="btn-card">
                Cancel
              </Button>
              <Button onClick={handleConfirmDelete} variant="contained" className="btn-card danger">
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      </div>
    </div>
  );
}

export default RequestReportPage;